import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { map } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { StatGroupComponent, StatItem } from '../../components/stat-group/stat-group.component';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { CodeIdChipComponent } from '../../components/code-id-chip/code-id-chip.component';
import { LifecyclePillComponent, LifecycleKind } from '../../components/lifecycle-pill/lifecycle-pill.component';
import { SearchBarComponent } from '../../components/search-bar/search-bar.component';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import { TestModuleService } from '../../services/test-module.service';
import { ProjectRoleService } from '../../services/project-role.service';
import { SavedViewService } from '../../services/saved-view.service';
import { PageSizePreferenceService } from '../../services/page-size-preference.service';
import { TestModule, TestModuleStatus, TEST_MODULE_STATUSES } from '../../models/test-module.model';
import { SavedView } from '../../models/saved-view.model';
import {
  FilterOption,
  FilterChange,
  ModuleFilterBarComponent,
  ModuleFilterKey,
} from './module-filter-bar/module-filter-bar.component';
import { SavedViewOption } from '../../shared/saved-views/saved-views-menu.component';
import { buildModuleSavedViewFiltersJson, parseModuleSavedViewFilters } from './module.utils';
import { ListFocusScrollDirective } from '../../shared/scroll-container/list-focus-scroll.directive';

interface ModuleRow {
  id: string;
  displayId: string;
  title: string;
  description: string;
  stepCount: number;
  usageCount: number;
  status: TestModuleStatus;
  statusLabel: string;
  statusClass: string;
  statusKind: LifecycleKind;
  authorName: string | null;
}

// Der Design-Mock (page-module.jsx:192) nutzt hier zwar `TcStatusPill`, das ist
// aber semantisch der Ausfuehrungs-Status und wuerde `DEPRECATED` rot als
// "Fehlgeschlagen" faerben (sgraetz-Review NEX-1086 C2). Die BE-Enum
// `TestModuleStatus` ist ein Lifecycle-Status — `LifecyclePill` traegt exakt
// diese Semantik, teilt sich die Farbwerte mit der Testfaelle-Tabelle
// (die dieselbe Enum konsumiert) und haelt die zwei Uebersichten optisch konsistent.
const MODULE_STATUS_TO_LIFECYCLE: Record<TestModuleStatus, LifecycleKind> = {
  DRAFT: 'draft',
  READY: 'ready',
  ACTIVE: 'active',
  DEPRECATED: 'deprecated',
};

const STATUS_ORDER: readonly TestModuleStatus[] = TEST_MODULE_STATUSES;
const STATUS_RANK: Record<TestModuleStatus, number> = { DRAFT: 0, READY: 1, ACTIVE: 2, DEPRECATED: 3 };

export type ModuleSortCol = 'displayId' | 'title' | 'stepCount' | 'usageCount' | 'status' | 'authorName';
type SortDir = 'asc' | 'desc';
interface TableSort {
  col: ModuleSortCol | null;
  dir: SortDir;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_ROUTE_KEY = 'test-modules';

// Jede Status-Kachel zählt strikt genau einen Status wie das Bundle
// (page-module.jsx:162). Das Bundle zeigt nur Gesamt · Aktiv · Entwürfe; Bereit und
// Veraltet sind eine bewusste Abweichung (NEX-1380), damit die Status-Kacheln sich
// zur Gesamtzahl summieren. Farben wie im Bundle (page-module.jsx:4-9, 162-163).
const STAT_TILE_ORDER: readonly TestModuleStatus[] = ['ACTIVE', 'READY', 'DRAFT', 'DEPRECATED'];
const STAT_TILES: Record<TestModuleStatus, { labelKey: string; color: string }> = {
  ACTIVE: { labelKey: 'modules.stats.active', color: 'var(--to-green-fg, #15803d)' },
  READY: { labelKey: 'modules.stats.ready', color: 'var(--to-blue-fg, #1e40af)' },
  DRAFT: { labelKey: 'modules.stats.draft', color: 'var(--to-text-faint, #9ca3af)' },
  DEPRECATED: { labelKey: 'modules.stats.deprecated', color: 'var(--to-warn-fg, #92400e)' },
};

@Component({
  selector: 'app-module',
  imports: [
    ListFocusScrollDirective,
    TranslatePipe,
    MatButton,
    MatIcon,
    MatProgressSpinner,
    ProjectNavComponent,
    PageHeaderComponent,
    StatGroupComponent,
    AvatarComponent,
    CodeIdChipComponent,
    LifecyclePillComponent,
    SearchBarComponent,
    ConfirmDialogComponent,
    ModuleFilterBarComponent,
  ],
  templateUrl: './module.component.html',
  styleUrl: './module.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModuleComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly moduleService = inject(TestModuleService);
  private readonly savedViewService = inject(SavedViewService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly projectRoleService = inject(ProjectRoleService);
  private readonly pageSizePref = inject(PageSizePreferenceService);

  readonly isProjectReader = this.projectRoleService.isProjectReader;

  private readonly currentLang = toSignal(this.translate.onLangChange.pipe(map((e) => e.lang)), {
    initialValue: this.translate.currentLang,
  });

  readonly projectId: string;

  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly deleteError = signal(false);
  readonly deleting = signal(false);
  readonly searchTerm = signal('');

  readonly selectedStatus = signal<string[]>([]);
  readonly selectedAuthor = signal<string[]>([]);

  readonly savedViews = signal<SavedView[]>([]);
  readonly activeSavedViewName = signal<string | null>(null);
  readonly savedViewsError = signal<string | null>(null);

  readonly savedViewsOptions = computed<SavedViewOption[]>(() =>
    this.savedViews().map((v) => ({ name: v.name, summary: this.summarizeSavedView(v) })),
  );

  readonly showDeleteModal = signal(false);
  readonly rowToDelete = signal<ModuleRow | null>(null);

  readonly pageIndex = signal(0);
  readonly pageSize = signal(this.pageSizePref.get(PAGE_SIZE_ROUTE_KEY, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE));
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  private readonly modules = signal<TestModule[]>([]);

  readonly rows = computed<ModuleRow[]>(() => {
    this.currentLang();
    return this.modules().map((m) => this.toRow(m));
  });

  private readonly statusCounts = computed<Record<TestModuleStatus, number>>(() => {
    const all = this.modules();
    return Object.fromEntries(
      TEST_MODULE_STATUSES.map((status) => [status, all.filter((m) => m.status === status).length]),
    ) as Record<TestModuleStatus, number>;
  });

  readonly statItems = computed<StatItem[]>(() => {
    this.currentLang();
    const counts = this.statusCounts();
    return [
      { label: this.translate.instant('modules.stats.total'), value: this.modules().length },
      ...STAT_TILE_ORDER.map((status) => ({
        label: this.translate.instant(STAT_TILES[status].labelKey),
        value: counts[status],
        color: STAT_TILES[status].color,
      })),
    ];
  });

  readonly statsAriaLabel = computed(() => {
    this.currentLang();
    const counts = this.statusCounts();
    return this.translate.instant('modules.stats.ariaLabel', {
      total: this.modules().length,
      active: counts.ACTIVE,
      ready: counts.READY,
      draft: counts.DRAFT,
      deprecated: counts.DEPRECATED,
    });
  });

  readonly statusOptions = computed<FilterOption[]>(() => {
    this.currentLang();
    const present = new Set(this.rows().map((r) => r.status));
    return STATUS_ORDER.filter((s) => present.has(s)).map((s) => ({
      value: s,
      label: this.translate.instant(`modules.status.${s}`),
    }));
  });

  readonly authorOptions = computed<FilterOption[]>(() => toLabelOptions(this.rows().map((r) => r.authorName)));

  readonly filteredRows = computed<ModuleRow[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const status = this.selectedStatus();
    const author = this.selectedAuthor();

    return this.rows().filter((row) => {
      if (
        term &&
        !row.title.toLowerCase().includes(term) &&
        !row.displayId.toLowerCase().includes(term) &&
        !row.description.toLowerCase().includes(term)
      ) {
        return false;
      }
      if (status.length && !status.includes(row.status)) return false;
      if (author.length && (row.authorName === null || !author.includes(row.authorName))) return false;
      return true;
    });
  });

  readonly total = computed(() => this.filteredRows().length);
  readonly unfilteredTotal = computed(() => this.modules().length);

  // NEX-1058 Review (fboudouhi): Basis fuer den Reset-Button in der Filter-Bar.
  readonly hasActiveFilters = computed(
    () => this.searchTerm().length > 0 || this.selectedStatus().length > 0 || this.selectedAuthor().length > 0,
  );

  readonly tableSort = signal<TableSort>({ col: null, dir: 'asc' });

  readonly sortedRows = computed<ModuleRow[]>(() => sortRows(this.filteredRows(), this.tableSort()));

  readonly pagedRows = computed<ModuleRow[]>(() => {
    const start = this.pageIndex() * this.pageSize();
    return this.sortedRows().slice(start, start + this.pageSize());
  });

  readonly rangeStart = computed(() => (this.total() === 0 ? 0 : this.pageIndex() * this.pageSize() + 1));
  readonly rangeEnd = computed(() => Math.min(this.total(), (this.pageIndex() + 1) * this.pageSize()));
  readonly pageCount = computed(() => Math.max(1, Math.ceil(this.total() / this.pageSize())));

  readonly hasResults = computed(() => this.total() > 0);

  readonly resultCountKey = computed(() =>
    this.unfilteredTotal() === 1 ? 'modules.resultCountOne' : 'modules.resultCountOther',
  );

  constructor() {
    this.projectId = this.route.snapshot.params['projectId'];
  }

  ngOnInit(): void {
    this.loadModules();
    this.loadSavedViews();
  }

  private loadSavedViews(): void {
    this.savedViewService.list(this.projectId, 'MODULES').subscribe({
      next: (views) => this.savedViews.set(views),
      error: () => this.savedViews.set([]),
    });
  }

  private summarizeSavedView(view: SavedView): string {
    const filters = parseModuleSavedViewFilters(view.filtersJson);
    const parts = [
      this.describeEnumFilter(filters.status, 'modules.status'),
      this.describeFreetextFilter(filters.author),
    ].filter((s): s is string => !!s);
    if (parts.length === 0) {
      return this.translate.instant('modules.savedViews.summaryAll');
    }
    return parts.join(' · ');
  }

  private describeEnumFilter(values: string[], i18nPrefix: string): string | null {
    if (values.length === 0) return null;
    return values.map((v) => this.translate.instant(`${i18nPrefix}.${v}`)).join(', ');
  }

  private describeFreetextFilter(values: string[]): string | null {
    return values.length === 0 ? null : values.join(', ');
  }

  onSavedViewSelected(name: string): void {
    const view = this.savedViews().find((v) => v.name === name);
    if (!view) return;
    const filters = parseModuleSavedViewFilters(view.filtersJson);
    this.searchTerm.set(filters.query ?? '');
    // NEX-1058 Review (fboudouhi Blocker 1): eine vor der Single-Select-
    // Umstellung gespeicherte Ansicht kann pro Filter mehrere Werte tragen.
    // Auf den ersten Wert kappen — sonst zeigt das Dropdown „Entwurf",
    // waehrend die Tabelle DRAFT und READY filtert.
    this.selectedStatus.set(filters.status.slice(0, 1));
    this.selectedAuthor.set(filters.author.slice(0, 1));
    this.pageIndex.set(0);
    this.activeSavedViewName.set(view.name);
  }

  onSavedViewSaved(name: string): void {
    const filtersJson = buildModuleSavedViewFiltersJson({
      query: this.searchTerm() || null,
      status: this.selectedStatus(),
      author: this.selectedAuthor(),
    });
    this.savedViewService.upsert(this.projectId, { name, context: 'MODULES', filtersJson }).subscribe({
      next: (saved) => {
        this.savedViews.update((list) => {
          const rest = list.filter((v) => v.name !== saved.name);
          return [...rest, saved].sort((a, b) => a.name.localeCompare(b.name));
        });
        this.activeSavedViewName.set(saved.name);
        this.savedViewsError.set(null);
      },
      error: () => this.savedViewsError.set(this.translate.instant('modules.savedViews.saveError')),
    });
  }

  /**
   * Das Loeschen selbst uebernimmt der generische SavedViewsMenuComponent (NEX-786) —
   * hier wird nur noch der lokale State nach einem erfolgreichen Loeschvorgang bereinigt.
   */
  onSavedViewDeleted(name: string): void {
    this.savedViews.update((list) => list.filter((v) => v.name !== name));
    if (this.activeSavedViewName() === name) {
      this.activeSavedViewName.set(null);
    }
  }

  onSavedViewDeleteFailed(): void {
    this.savedViewsError.set(this.translate.instant('modules.savedViews.deleteError'));
  }

  private clearActiveView(): void {
    if (this.activeSavedViewName() !== null) {
      this.activeSavedViewName.set(null);
    }
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
    this.pageIndex.set(0);
    this.clearActiveView();
  }

  onFilterChange({ key, value }: FilterChange): void {
    this.filterSignal(key).set(value ? [value] : []);
    this.pageIndex.set(0);
    this.clearActiveView();
  }

  // NEX-1058 Review (fboudouhi): setzt Suche und alle Filter zurueck.
  onResetFilters(): void {
    this.searchTerm.set('');
    this.selectedStatus.set([]);
    this.selectedAuthor.set([]);
    this.pageIndex.set(0);
    this.clearActiveView();
  }

  private filterSignal(key: ModuleFilterKey) {
    return key === 'status' ? this.selectedStatus : this.selectedAuthor;
  }

  private pruneStaleFilters(): void {
    const keep = (signalRef: typeof this.selectedStatus, options: FilterOption[]): void => {
      const available = new Set(options.map((o) => o.value));
      const next = signalRef().filter((v) => available.has(v));
      if (next.length !== signalRef().length) signalRef.set(next);
    };
    keep(this.selectedStatus, this.statusOptions());
    keep(this.selectedAuthor, this.authorOptions());
  }

  sortIcon(col: ModuleSortCol): string {
    const s = this.tableSort();
    if (s.col !== col) return 'unfold_more';
    return s.dir === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  sortAria(col: ModuleSortCol): 'ascending' | 'descending' | 'none' {
    const s = this.tableSort();
    if (s.col !== col) return 'none';
    return s.dir === 'asc' ? 'ascending' : 'descending';
  }

  sortBy(col: ModuleSortCol): void {
    const current = this.tableSort();
    this.tableSort.set({ col, dir: current.col === col && current.dir === 'asc' ? 'desc' : 'asc' });
    this.pageIndex.set(0);
  }

  onPageChange(index: number): void {
    this.pageIndex.set(index);
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.pageIndex.set(0);
    this.pageSizePref.set(PAGE_SIZE_ROUTE_KEY, size, PAGE_SIZE_OPTIONS);
  }

  onCreate(): void {
    this.router.navigate(['/projects', this.projectId, 'test-modules', 'new']);
  }

  onEdit(row: ModuleRow): void {
    this.router.navigate(['/projects', this.projectId, 'test-modules', row.id, 'edit']);
  }

  onRowKeydown(event: Event, row: ModuleRow): void {
    if (event.target !== event.currentTarget) return;
    this.onEdit(row);
  }

  onDeleteRequest(row: ModuleRow): void {
    this.rowToDelete.set(row);
    this.showDeleteModal.set(true);
  }

  onCancelDelete(): void {
    this.showDeleteModal.set(false);
    this.rowToDelete.set(null);
  }

  onConfirmDelete(): void {
    const row = this.rowToDelete();
    if (!row) return;
    this.deleteError.set(false);
    this.deleting.set(true);
    this.moduleService
      .delete(this.projectId, row.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.modules.set(this.modules().filter((m) => m.id !== row.id));
          this.pruneStaleFilters();
          this.deleting.set(false);
          this.showDeleteModal.set(false);
          this.rowToDelete.set(null);
          if (this.pageIndex() >= this.pageCount()) {
            this.pageIndex.set(this.pageCount() - 1);
          }
        },
        error: (err: HttpErrorResponse) => {
          // 409 = Modul noch in anderen Modulen/Testfällen eingebunden: Modul
          // bleibt erhalten, Inline-Fehler wird angezeigt.
          this.deleting.set(false);
          this.showDeleteModal.set(false);
          this.rowToDelete.set(null);
          this.deleteConflict.set(err.status === 409);
          this.deleteError.set(true);
        },
      });
  }

  readonly deleteConflict = signal(false);

  readonly deleteErrorKey = computed(() =>
    this.deleteConflict() ? 'modules.deleteConflictError' : 'modules.deleteError',
  );

  readonly deleteMessage = computed(() => {
    this.currentLang();
    const row = this.rowToDelete();
    if (!row) return '';
    const base = this.translate.instant('modules.deleteMessage', { title: row.title });
    if (row.usageCount > 0) {
      return `${base} ${this.translate.instant('modules.deleteUsageHint', { count: row.usageCount })}`;
    }
    return base;
  });

  private loadModules(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.moduleService
      .list(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (modules) => {
          this.modules.set(modules);
          this.pruneStaleFilters();
          this.loading.set(false);
        },
        error: () => {
          this.modules.set([]);
          this.pruneStaleFilters();
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  private toRow(module: TestModule): ModuleRow {
    const title = (module.name ?? '').trim();
    return {
      id: module.id,
      displayId: module.key,
      title: title || this.translate.instant('modules.noTitle'),
      description: (module.description ?? '').trim(),
      stepCount: module.stepCount ?? 0,
      usageCount: module.usageCount ?? 0,
      status: module.status,
      statusLabel: this.translate.instant(`modules.status.${module.status}`),
      statusKind: MODULE_STATUS_TO_LIFECYCLE[module.status] ?? 'draft',
      statusClass: statusPillClass(module.status),
      authorName: module.authorName?.trim() || null,
    };
  }
}

function statusPillClass(status: TestModuleStatus): string {
  return `mod-pill mod-pill--status-${status.toLowerCase()}`;
}

function sortRows(rows: ModuleRow[], sort: TableSort): ModuleRow[] {
  const { col, dir } = sort;
  if (!col) return rows;
  const copy = [...rows];
  copy.sort((a, b) => {
    let cmp: number;
    switch (col) {
      case 'status':
        cmp = STATUS_RANK[a.status] - STATUS_RANK[b.status];
        break;
      case 'stepCount':
        cmp = a.stepCount - b.stepCount;
        break;
      case 'usageCount':
        cmp = a.usageCount - b.usageCount;
        break;
      case 'displayId':
        cmp = a.displayId.localeCompare(b.displayId, undefined, { numeric: true, sensitivity: 'base' });
        break;
      default:
        cmp = (a[col] ?? '').localeCompare(b[col] ?? '', undefined, { sensitivity: 'base' });
    }
    return dir === 'asc' ? cmp : -cmp;
  });
  return copy;
}

// Freitext-Werte (Autor) zu eindeutigen, alphabetisch sortierten Filter-Optionen
// verdichten; Label entspricht dem Wert. null wird verworfen.
function toLabelOptions(values: (string | null)[]): FilterOption[] {
  const distinct = [...new Set(values.filter((v): v is string => v !== null))].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }),
  );
  return distinct.map((value) => ({ value, label: value }));
}
